/**
 * Auth bootstrap helpers (BUG-008 / DEV-320).
 *
 * While `/api/auth/user` is in flight the app used to render the full
 * Layout, whose header showed "Sign In" because `isAuthenticated` is false
 * until the query resolves — so a slow session lookup looked like a logout.
 * These pure helpers drive (a) whether the header may show a Sign In
 * control and (b) react-query's retry decision for the auth fetch.
 */

/** Error thrown by the auth fetch; carries the HTTP status when there was one. */
export class AuthFetchError extends Error {
  readonly status: number | undefined;

  constructor(message: string, status?: number) {
    super(message);
    this.name = "AuthFetchError";
    this.status = status;
  }
}

/** Max retries for the auth fetch after a network/5xx failure. */
export const AUTH_FETCH_MAX_RETRIES = 1;

/**
 * react-query `retry` predicate for `/api/auth/user`.
 *
 * One retry for transport failures (no status) and 5xx; never for 4xx — a
 * 401 means the session is gone and the user must land on Login right away.
 */
export function shouldRetryAuthFetch(failureCount: number, error: unknown): boolean {
  if (failureCount >= AUTH_FETCH_MAX_RETRIES) return false;
  const status = getErrorStatus(error);
  if (status === undefined) return true; // network / fetch threw
  return status >= 500;
}

/**
 * The header's Sign In control is only meaningful once auth has settled AND
 * nobody is signed in. While loading, nothing auth-related should render.
 */
export function showSignInControl(state: { isLoading: boolean; isAuthenticated: boolean }): boolean {
  return !state.isLoading && !state.isAuthenticated;
}

function getErrorStatus(error: unknown): number | undefined {
  if (error instanceof AuthFetchError) return error.status;
  const status = (error as { status?: unknown } | null | undefined)?.status;
  return typeof status === "number" ? status : undefined;
}
